/**
 * Time entry history list.
 * Shows past time entries for the contractor.
 */

import { useTimeEntries } from '../hooks/useTimeEntries';
import { formatDuration } from '../lib/format';
import { TaskCardSkeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import type { TimeEntry } from '../lib/api';

interface TimeEntryListProps {
  taskId?: string;
}

export function TimeEntryList({ taskId }: TimeEntryListProps) {
  const { entries, total, isLoading, isError, error, refetch } = useTimeEntries(
    { taskId, limit: 50 }
  );

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <TimeEntrySkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4">
        <ErrorState error={error} onRetry={refetch} />
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="p-4">
        <EmptyTimeEntries />
      </div>
    );
  }

  // Group entries by date
  const groupedEntries = groupEntriesByDate(entries);

  return (
    <div className="p-4 space-y-6">
      {Object.entries(groupedEntries).map(([date, dayEntries]) => (
        <div key={date}>
          <h3 className="text-xs font-semibold text-ink-subtle uppercase tracking-wider mb-3">
            {formatDateHeader(date)}
          </h3>
          <div className="space-y-2">
            {dayEntries.map((entry) => (
              <TimeEntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        </div>
      ))}

      {total > entries.length && (
        <p className="text-center text-sm text-ink-subtle">
          Showing {entries.length} of {total} entries
        </p>
      )}
    </div>
  );
}

interface TimeEntryCardProps {
  entry: TimeEntry;
}

function TimeEntryCard({ entry }: TimeEntryCardProps) {
  const startTime = new Date(entry.startTime);
  const endTime = entry.endTime ? new Date(entry.endTime) : null;
  const isActive = !entry.endTime;

  return (
    <div
      className={`
        bg-surface-raised border rounded-lg p-3
        ${isActive ? 'border-status-in-progress/50' : 'border-rule'}
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Task info */}
          {entry.task && (
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-sm font-semibold text-ink">
                {entry.task.gpSalesOrderNumber}
              </span>
              <span className="text-ink-subtle">·</span>
              <span className="font-mono text-xs text-ink-muted truncate">
                {entry.task.partNumber}
              </span>
            </div>
          )}

          {/* Time range */}
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <span>{formatTime(startTime)}</span>
            <span className="text-ink-subtle">→</span>
            {endTime ? (
              <span>{formatTime(endTime)}</span>
            ) : (
              <span className="text-status-in-progress">In progress</span>
            )}
          </div>
        </div>

        {/* Duration */}
        <div className="flex-none text-right">
          {entry.durationMinutes != null ? (
            <span className="font-mono text-sm font-semibold text-ink">
              {formatDuration(entry.durationMinutes)}
            </span>
          ) : isActive ? (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-status-in-progress animate-pulse" />
              <span className="text-sm text-status-in-progress font-medium">
                Active
              </span>
            </span>
          ) : (
            <span className="text-sm text-ink-subtle">-</span>
          )}
        </div>
      </div>

      {/* Sync status indicator */}
      {entry.syncStatus === 'conflict' && (
        <div className="mt-2 pt-2 border-t border-rule">
          <span className="text-xs text-amber-400">
            Sync conflict - pending resolution
          </span>
        </div>
      )}
    </div>
  );
}

function TimeEntrySkeleton() {
  return (
    <div className="bg-surface-raised border border-rule rounded-lg p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <div className="skeleton h-4 w-32" />
          <div className="skeleton h-3 w-24" />
        </div>
        <div className="skeleton h-5 w-12" />
      </div>
    </div>
  );
}

function EmptyTimeEntries() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 mb-4 rounded-xl bg-surface-highlight flex items-center justify-center">
        <ClockIcon className="w-6 h-6 text-ink-subtle" />
      </div>
      <h3 className="text-base font-semibold text-ink mb-1">No time entries</h3>
      <p className="text-sm text-ink-muted">
        Start working on a task to log time.
      </p>
    </div>
  );
}

// Utility functions
function groupEntriesByDate(
  entries: TimeEntry[]
): Record<string, TimeEntry[]> {
  const groups: Record<string, TimeEntry[]> = {};

  for (const entry of entries) {
    const date = new Date(entry.startTime).toISOString().split('T')[0]!;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
  }

  return groups;
}

function formatDateHeader(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.getTime() === today.getTime()) {
    return 'Today';
  }
  if (date.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 6v6l4 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
